// src/RankingsPage.js
import React, { useEffect, useState, useRef } from "react";
import "./App.css";
import { Link, useSearchParams } from "react-router-dom";

export default function OverallRankingsPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [metadata, setMetadata] = useState(null);
    
    // Initialize state from URL parameters
    const [currentPage, setCurrentPage] = useState(() => {
        const pageParam = searchParams.get('page');
        return pageParam ? parseInt(pageParam, 10) : 1;
    });
    const [activeOwner, setActiveOwner] = useState(() => searchParams.get('search') || null);
    const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') || '');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(() => searchParams.get('search') || '');
    const [selectedLanguages, setSelectedLanguages] = useState(() => {
        const langsParam = searchParams.get('langs');
        return langsParam ? langsParam.split(',').filter(l => l) : [];
    });
    const [showAll, setShowAll] = useState(() => {
        const langsParam = searchParams.get('langs');
        return !langsParam || langsParam === '';
    });
    
    const [repos, setRepos] = useState([]);
    const [hoveredRepo, setHoveredRepo] = useState(null);
    const [maxScore, setMaxScore] = useState(1);
    const [minScore, setMinScore] = useState(null);
    const [topScore, setTopScore] = useState(null);
    const [repoDetails, setRepoDetails] = useState({});
    const [languages, setLanguages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [pageCache, setPageCache] = useState({});
    const timeoutRef = useRef(null);
    const searchTimeoutRef = useRef(null);
    const [pageInput, setPageInput] = useState(null);
    const [searchMatchCounts, setSearchMatchCounts] = useState({});
    const [allSearchResults, setAllSearchResults] = useState([]); // Store ALL search results for client-side pagination
    const [ownerIndexCache, setOwnerIndexCache] = useState({});
    const [showFilters, setShowFilters] = useState(false);
    const [searchSuggestions, setSearchSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
    const pageBeforeSearchRef = useRef(1);
    const searchWrapperRef = useRef(null);
    const [tooltipPosition, setTooltipPosition] = useState({});
    const [updatingRows, setUpdatingRows] = useState(false);
    const [tableFlash, setTableFlash] = useState(false);
    // Skip scan animation on initial load if there's a search parameter
    const skipScanAnimationRef = useRef(searchParams.get('search') !== null);
    const previousFiltersRef = useRef({ languages: selectedLanguages, showAll, search: debouncedSearchQuery });
    const tableFlashTimeoutRef = useRef(null);
    const isInitialLoadRef = useRef(true); // Track if this is the initial page load
    const previousLangsParamRef = useRef(searchParams.get('langs'));

    // Use refs to track current values without causing re-renders
    const currentPageRef = useRef(currentPage);
    const debouncedSearchQueryRef = useRef(debouncedSearchQuery);
    
    useEffect(() => {
        currentPageRef.current = currentPage;
    }, [currentPage]);
    
    useEffect(() => {
        debouncedSearchQueryRef.current = debouncedSearchQuery;
    }, [debouncedSearchQuery]);

    // Load metadata
    useEffect(() => {
        fetch(`${process.env.PUBLIC_URL}/metadata.json`)
            .then((res) => res.json())
            .then((data) => {
                setMetadata(data);
                const langs = Object.keys(data.languages)
                    .filter((lang) => lang !== "Other" && lang !== "All" && lang !== "Python_PyPI")
                    .sort((a, b) => data.languages[b].total - data.languages[a].total);
                setLanguages(langs);
                
                // Get min/max scores from metadata (no need to load pages)
                const allData = data.languages.All;
                if (allData) {
                    if (allData.max_score !== undefined) {
                        setTopScore(allData.max_score);
                    }
                    if (allData.min_score !== undefined) {
                        setMinScore(allData.min_score);
                    }
                }
            })
            .catch((err) => console.error("❌ Failed to load metadata:", err));
    }, []);
    
    // Save scroll position before unload (for F5 refresh)
    useEffect(() => {
        const handleBeforeUnload = () => {
            sessionStorage.setItem('scrollPosition', window.pageYOffset.toString());
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, []);
    
    // Mark initial load as complete and restore scroll position after first data load
    useEffect(() => {
        if (repos.length > 0 && isInitialLoadRef.current) {
            isInitialLoadRef.current = false;
            
            // Restore scroll position with smooth animation after a short delay to ensure content is rendered
            const savedScrollPosition = sessionStorage.getItem('scrollPosition');
            if (savedScrollPosition) {
                setTimeout(() => {
                    window.scrollTo({ top: parseInt(savedScrollPosition, 10), behavior: 'smooth' });
                    sessionStorage.removeItem('scrollPosition');
                }, 100);
            }
        }
    }, [repos]);

    // Sync state when URL changes (browser back/forward)
    useEffect(() => {
        const searchParam = searchParams.get('search');
        const pageParam = searchParams.get('page');
        const langsParam = searchParams.get('langs');
        
        // Only update if values actually changed (to avoid infinite loops)
        if (searchParam !== debouncedSearchQueryRef.current) {
            const newSearch = searchParam || '';
            setSearchQuery(newSearch);
            setDebouncedSearchQuery(newSearch);
            setActiveOwner(newSearch || null);
            setShowSuggestions(false);
            if (newSearch) {
                skipScanAnimationRef.current = true;
            }
            if (!newSearch) {
                setSearchMatchCounts({});
            }
        }
        
        // Update language filter - only if langs param actually changed
        if (langsParam !== previousLangsParamRef.current) {
            previousLangsParamRef.current = langsParam;
            if (langsParam) {
                const langs = langsParam.split(',').filter(l => l);
                setSelectedLanguages(langs);
                setShowAll(langs.length === 0);
            } else if (!searchParam) {
                // Only reset languages if no search param either
                setSelectedLanguages([]);
                setShowAll(true);
            }
        }
        
        // Update page number
        if (pageParam) {
            const page = parseInt(pageParam, 10);
            if (!isNaN(page) && page > 0 && page !== currentPageRef.current) {
                setCurrentPage(page);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    // Generate search suggestions
    useEffect(() => {
        if (!searchQuery.trim()) {
            setSearchSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        // Don't show suggestions if search is already active (debounced query matches)
        if (searchQuery === debouncedSearchQuery) {
            setShowSuggestions(false);
            return;
        }

        const query = searchQuery.toLowerCase().trim();
        const firstChar = query[0] && query[0].match(/[a-z0-9]/) ? query[0] : 'other';
        
        // Load the index for this character if not already loaded
        const loadAndGenerateSuggestions = async () => {
            const suggestions = [];
            
            // Try to load owner index for this character
            if (!ownerIndexCache[firstChar]) {
                try {
                    const response = await fetch(`${process.env.PUBLIC_URL}/owner_index/${firstChar}.json`);
                    if (response.ok) {
                        const data = await response.json();
                        setOwnerIndexCache(prev => ({ ...prev, [firstChar]: data }));
                        
                        // Generate owner suggestions from the newly loaded data
                        const owners = Object.keys(data);
                        owners.forEach(owner => {
                            if (owner.toLowerCase().includes(query)) {
                                suggestions.push({ text: owner, type: 'owner', icon: '👤' });
                            }
                        });
                    }
                } catch (err) {
                    console.log(`Failed to load suggestions for '${firstChar}'`);
                }
            } else {
                // Use cached owner data
                const owners = Object.keys(ownerIndexCache[firstChar]);
                owners.forEach(owner => {
                    if (owner.toLowerCase().includes(query)) {
                        suggestions.push({ text: owner, type: 'owner', icon: '👤' });
                    }
                });
            }
            
            // Add languages from Other category (not in Top 10)
            // Sorted by popularity - more popular languages are easier to match
            const otherLanguages = [
                // Tier 1: Very popular (5000+ projects) - match with 1+ chars
                { name: 'Go', count: 8129 }, { name: 'C', count: 8101 }, { name: 'Shell', count: 7437 },
                { name: 'Rust', count: 5884 }, { name: 'Swift', count: 5536 }, { name: 'PHP', count: 5181 }, { name: 'R', count: 5122 },
                
                // Tier 2: Popular (1000-5000 projects) - match with 2+ chars
                { name: 'Objective-C', count: 2361 }, { name: 'Kotlin', count: 1965 }, { name: 'TeX', count: 1623 },
                { name: 'Scala', count: 1487 }, { name: 'Lua', count: 1327 }, { name: 'Dockerfile', count: 1218 },
                { name: 'PowerShell', count: 1151 }, { name: 'Vue', count: 1131 }, { name: 'Matlab', count: 1034 },
                
                // Tier 3: Medium (400-1000 projects) - match with 2+ chars
                { name: 'HCL', count: 963 }, { name: 'SCSS', count: 918 }, { name: 'Makefile', count: 904 },
                { name: 'Dart', count: 901 }, { name: 'Haskell', count: 885 }, { name: 'Perl', count: 822 },
                { name: 'Clojure', count: 801 }, { name: 'MATLAB', count: 732 }, { name: 'CoffeeScript', count: 730 },
                { name: 'Elixir', count: 692 }, { name: 'Assembly', count: 581 }, { name: 'Vim script', count: 546 },
                { name: 'Emacs Lisp', count: 504 }, { name: 'Arduino', count: 488 }, { name: 'Julia', count: 430 },
                { name: 'VimL', count: 429 }, { name: 'GDScript', count: 422 }, { name: 'Svelte', count: 402 },
                
                // Tier 4: Less common (100-400 projects) - match with 3+ chars
                { name: 'Processing', count: 392 }, { name: 'CMake', count: 309 }, { name: 'Nix', count: 308 },
                { name: 'Vim Script', count: 296 }, { name: 'Racket', count: 277 }, { name: 'Astro', count: 257 },
                { name: 'ShaderLab', count: 252 }, { name: 'EJS', count: 250 }, { name: 'TSQL', count: 246 },
                { name: 'Groovy', count: 236 }, { name: 'Solidity', count: 233 }, { name: 'OCaml', count: 204 },
                { name: 'Verilog', count: 196 }, { name: 'F#', count: 184 }, { name: 'ASP', count: 184 },
                { name: 'Erlang', count: 175 }, { name: 'Handlebars', count: 171 }, { name: 'Cuda', count: 169 },
                { name: 'Tcl', count: 164 }, { name: 'Elm', count: 157 }, { name: 'Fortran', count: 152 },
                { name: 'Zig', count: 140 }, { name: 'Common Lisp', count: 132 }, { name: 'Smarty', count: 130 },
                { name: 'AutoHotkey', count: 127 }, { name: 'VHDL', count: 125 }, { name: 'Batchfile', count: 121 },
                { name: 'Sass', count: 120 }, { name: 'Scheme', count: 117 }, { name: 'PureScript', count: 114 },
                { name: 'Vala', count: 112 }, { name: 'Crystal', count: 107 },
                
                // Tier 5: Rare (50-100 projects) - match with 4+ chars
                { name: 'GLSL', count: 99 }, { name: 'Roff', count: 99 }, { name: 'Less', count: 98 },
                { name: 'Haxe', count: 97 }, { name: 'Pascal', count: 95 }, { name: 'Nunjucks', count: 92 },
                { name: 'Nim', count: 88 }, { name: 'Haml', count: 87 }, { name: 'ReScript', count: 84 },
                { name: 'D', count: 82 }, { name: 'Terra', count: 78 }, { name: 'Odin', count: 76 },
                { name: 'PureBasic', count: 75 }, { name: 'Jsonnet', count: 74 }, { name: 'V', count: 73 },
                { name: 'SystemVerilog', count: 72 }, { name: 'Stylus', count: 71 }, { name: 'ActionScript', count: 69 },
                { name: 'PLpgSQL', count: 69 }, { name: 'Objective-C++', count: 68 }, { name: 'QML', count: 66 },
                { name: 'FreeMarker', count: 65 }, { name: 'WebAssembly', count: 63 }, { name: 'G-code', count: 63 },
                { name: 'AGS Script', count: 62 }, { name: 'Standard ML', count: 62 }, { name: 'Coq', count: 61 },
                { name: 'Starlark', count: 60 }, { name: 'sed', count: 59 }, { name: 'Rust', count: 59 },
                { name: 'Smalltalk', count: 57 }, { name: 'Liquid', count: 56 }, { name: 'Apex', count: 55 },
                { name: 'Prolog', count: 54 }, { name: 'OpenSCAD', count: 54 }, { name: 'Eagle', count: 53 },
                { name: 'Pug', count: 53 }, { name: 'Mako', count: 51 }, { name: 'Lean', count: 50 },
                
                // Tier 6: Very rare (<50 projects) - match with 5+ chars  
                { name: 'PostScript', count: 48 }, { name: 'Red', count: 47 }, { name: 'OpenQASM', count: 46 },
                { name: 'Blade', count: 46 }, { name: 'Pkl', count: 45 }, { name: 'Max', count: 45 },
                { name: 'Mercury', count: 44 }, { name: 'Awk', count: 44 }, { name: 'Forth', count: 43 },
                { name: 'HLSL', count: 42 }, { name: 'Bicep', count: 42 }, { name: 'XSLT', count: 41 },
                { name: 'Fennel', count: 40 }, { name: 'Idris', count: 39 }, { name: 'M4', count: 39 },
                { name: 'Cuda', count: 38 }, { name: 'MDX', count: 38 }, { name: 'Meson', count: 37 },
                { name: 'Jinja', count: 37 }, { name: 'Pony', count: 36 }, { name: 'Ballerina', count: 36 },
                { name: 'Agda', count: 36 }, { name: 'PEG.js', count: 35 }, { name: 'AppleScript', count: 35 },
                { name: 'Inno Setup', count: 34 }, { name: 'NASL', count: 34 }, { name: 'Ren\'Py', count: 33 },
                { name: 'Papyrus', count: 33 }, { name: 'Gnuplot', count: 33 }, { name: 'LLVM', count: 32 },
                { name: 'Frege', count: 32 }, { name: 'Factor', count: 31 }, { name: 'BitBake', count: 31 },
                { name: 'Gherkin', count: 30 }, { name: 'Ada', count: 30 }, { name: 'CartoCSS', count: 30 },
                { name: 'Chapel', count: 29 }, { name: 'Modelica', count: 29 }, { name: 'Nemerle', count: 28 },
                { name: 'nesC', count: 28 }, { name: 'KiCad Layout', count: 27 }, { name: 'Cirru', count: 27 },
                { name: 'q', count: 27 }, { name: 'Module Management System', count: 26 }, { name: 'LilyPond', count: 26 },
                { name: 'Dhall', count: 26 }, { name: 'AMPL', count: 25 }, { name: 'Logos', count: 25 },
                { name: 'Raku', count: 25 }, { name: 'ASP.NET', count: 24 }, { name: 'POV-Ray SDL', count: 24 },
                { name: 'Yacc', count: 24 }, { name: 'mcfunction', count: 23 }, { name: 'NewLisp', count: 23 },
                { name: 'X10', count: 23 }, { name: 'RPM Spec', count: 23 }, { name: 'Lex', count: 23 },
                { name: 'Wren', count: 22 }, { name: 'Alloy', count: 22 }, { name: 'EmberScript', count: 22 },
                { name: 'Brainfuck', count: 21 }, { name: 'Reason', count: 21 }, { name: 'SWIG', count: 21 },
                { name: 'TLA', count: 20 }, { name: 'Nearley', count: 20 }, { name: 'Io', count: 20 },
                { name: 'AutoIt', count: 20 }, { name: 'MATLAB', count: 19 }, { name: 'Gosu', count: 19 },
                { name: 'Monkey', count: 19 }, { name: 'POV-Ray', count: 19 }, { name: 'IDL', count: 19 },
                { name: 'Ring', count: 18 }, { name: 'Self', count: 18 }, { name: 'Dylan', count: 18 },
                { name: 'XQuery', count: 18 }, { name: 'RAML', count: 18 }, { name: 'xBase', count: 17 },
                { name: 'Component Pascal', count: 17 }, { name: 'Sage', count: 17 }, { name: 'NetLinx', count: 17 },
                { name: 'FLUX', count: 16 }, { name: 'LiveScript', count: 16 }, { name: 'XC', count: 16 },
                { name: 'Thrift', count: 16 }, { name: 'P4', count: 16 }, { name: 'mupad', count: 15 },
                { name: 'DriveIt', count: 15 }, { name: 'J', count: 15 }, { name: 'Harbour', count: 15 },
                { name: 'FreeBasic', count: 15 }, { name: 'M', count: 15 }, { name: 'Befunge', count: 14 },
                { name: 'JFlex', count: 14 }, { name: 'KRL', count: 14 }, { name: 'Hack', count: 14 },
                { name: 'VBA', count: 14 }, { name: 'Logtalk', count: 13 }, { name: 'Grammatical Framework', count: 13 },
                { name: 'CWeb', count: 13 }, { name: 'Fantom', count: 13 }, { name: 'Oxygene', count: 13 },
                { name: 'API Blueprint', count: 13 }, { name: 'Clean', count: 13 }, { name: 'Csound', count: 13 },
                { name: 'Charity', count: 12 }, { name: 'HolyC', count: 12 }, { name: 'SuperCollider', count: 12 },
                { name: 'DM', count: 12 }, { name: 'SourcePawn', count: 12 }, { name: 'Genie', count: 12 },
                { name: 'Turing', count: 11 }, { name: 'Ant Build System', count: 11 }, { name: 'Boo', count: 11 },
                { name: 'DataWeave', count: 11 }, { name: 'Csound Document', count: 11 }, { name: 'ANTLR', count: 11 },
                { name: 'Shen', count: 10 }, { name: 'OpenEdge ABL', count: 10 }, { name: 'Isabelle', count: 10 },
                { name: 'SaltStack', count: 10 }, { name: 'Cool', count: 10 }, { name: 'Latte', count: 10 },
                { name: 'Berry', count: 9 }, { name: 'VBScript', count: 9 }, { name: 'BlitzMax', count: 9 },
                { name: 'SQF', count: 9 }, { name: 'Motorola 68K Assembly', count: 9 }, { name: 'Rebol', count: 9 },
                { name: 'Classic ASP', count: 9 }, { name: 'PLSQL', count: 9 }, { name: 'GCC Machine Description', count: 9 },
                { name: 'Clarion', count: 8 }, { name: 'Click', count: 8 }, { name: 'Nu', count: 8 },
                { name: 'Pike', count: 8 }, { name: 'Lingo', count: 8 }, { name: 'Squirrel', count: 8 },
                { name: 'Euphoria', count: 8 }, { name: 'Zimpl', count: 8 }, { name: 'DTrace', count: 8 },
                { name: 'Cycript', count: 7 }, { name: 'Visual Basic', count: 7 }, { name: 'Game Maker Language', count: 7 },
                { name: 'Bison', count: 7 }, { name: 'Bluespec', count: 7 }, { name: 'Brightscript', count: 7 },
                { name: 'E', count: 7 }, { name: 'MAXScript', count: 7 }, { name: 'Hy', count: 7 },
                { name: 'UnrealScript', count: 6 }, { name: 'Oz', count: 6 }, { name: 'Pure Data', count: 6 },
                { name: 'mirah', count: 6 }, { name: 'Squeak', count: 6 }, { name: 'ChucK', count: 6 },
                { name: 'Lasso', count: 6 }, { name: 'Asymptote', count: 6 }, { name: 'REXX', count: 6 },
                { name: 'OpenCL', count: 6 }, { name: 'IGOR Pro', count: 5 }, { name: 'Limbo', count: 5 },
                { name: 'Metal', count: 5 }, { name: 'Smali', count: 5 }, { name: 'MoonScript', count: 5 },
                { name: 'X++', count: 5 }, { name: 'Turbo Pascal', count: 5 }, { name: 'ECL', count: 5 },
                { name: 'Slash', count: 5 }, { name: 'ZIL', count: 5 }, { name: 'Stan', count: 5 },
                { name: 'ATS', count: 4 }, { name: 'Ioke', count: 4 }, { name: 'LabVIEW', count: 4 },
                { name: 'Jolie', count: 4 }, { name: 'ColdFusion', count: 4 }, { name: 'Parrot', count: 4 },
                { name: 'Gettext Catalog', count: 4 }, { name: 'Objective-J', count: 4 }, { name: 'TXL', count: 4 },
                { name: 'LSL', count: 4 }, { name: 'BlitzBasic', count: 4 }, { name: 'ABAP', count: 4 },
                { name: 'TypeLanguage', count: 3 }, { name: 'Eiffel', count: 3 }, { name: 'Claire', count: 3 },
                { name: 'Ragel', count: 3 }, { name: 'Singularity', count: 3 }, { name: 'PicoLisp', count: 3 },
                { name: 'Opa', count: 3 }, { name: 'wisp', count: 3 }, { name: 'NCL', count: 3 },
                { name: 'Amulet', count: 3 }, { name: 'AspectJ', count: 3 }, { name: 'RobotFramework', count: 3 },
                { name: 'KiCad Schematic', count: 2 }, { name: 'XPROC', count: 2 }, { name: 'Mathematica', count: 2 },
                { name: 'YARA', count: 2 }, { name: 'ooc', count: 2 }, { name: 'AngelScript', count: 2 },
                { name: 'Inform 7', count: 2 }, { name: 'Makefile', count: 2 }, { name: 'MQL4', count: 2 },
                { name: 'Mirah', count: 2 }, { name: 'ColdFusion CFC', count: 2 }, { name: 'ChucKs', count: 2 },
                { name: 'Windows Registry Entries', count: 2 }, { name: 'MQL5', count: 2 }, { name: 'DIGITAL Command Language', count: 2 },
                { name: 'Monkey C', count: 1 }, { name: 'Scilab', count: 1 }, { name: 'CLIPS', count: 1 },
                { name: 'Puppet', count: 1 }, { name: 'Verona', count: 1 }, { name: 'Volt', count: 1 },
                { name: 'RenderScript', count: 1 }, { name: 'Zimpl', count: 1 }, { name: 'Groovy Server Pages', count: 1 },
                { name: 'DenizenScript', count: 1 }, { name: 'RUNOFF', count: 1 }, { name: 'WDL', count: 1 }
            ];
            
            // Filter languages based on query length and popularity
            otherLanguages.forEach(langObj => {
                const lang = langObj.name;
                const count = langObj.count;
                const langLower = lang.toLowerCase();
                
                // Determine minimum match length based on popularity
                let minMatchLength;
                if (count >= 5000) minMatchLength = 1;
                else if (count >= 1000) minMatchLength = 2;
                else if (count >= 400) minMatchLength = 2;
                else if (count >= 100) minMatchLength = 3;
                else if (count >= 50) minMatchLength = 4;
                else minMatchLength = 5;
                
                // Match if query is long enough and language contains the query
                if (query.length >= minMatchLength && langLower.includes(query)) {
                    suggestions.push({ text: lang, type: 'language', icon: '💻' });
                }
            });
            
            // Add popular topic suggestions
            const popularTopics = [
                'machine-learning', 'deep-learning', 'artificial-intelligence', 'neural-networks',
                'react', 'angular', 'docker', 'kubernetes',
                'blockchain', 'cryptocurrency', 'web3',
                'database', 'sql', 'nosql', 'mongodb', 'postgresql',
                'testing', 'automation', 'ci-cd', 'devops',
                'security', 'authentication', 'encryption'
            ];
            
            popularTopics.forEach(topic => {
                if (topic.toLowerCase().includes(query)) {
                    suggestions.push({ text: topic, type: 'topic', icon: '🏷️' });
                }
            });
            
            // Sort: owners first, then languages, then topics; all alphabetically within type
            suggestions.sort((a, b) => {
                const typeOrder = { 'owner': 0, 'language': 1, 'topic': 2 };
                if (a.type !== b.type) return typeOrder[a.type] - typeOrder[b.type];
                return a.text.localeCompare(b.text);
            });
            
            // Limit each type to ensure diversity: max 4 owners, 3 languages, 2 topics
            const limitedSuggestions = [];
            const typeCounts = { 'owner': 0, 'language': 0, 'topic': 0 };
            const typeMaxCounts = { 'owner': 4, 'language': 3, 'topic': 2 };
            
            for (const suggestion of suggestions) {
                if (typeCounts[suggestion.type] < typeMaxCounts[suggestion.type]) {
                    limitedSuggestions.push(suggestion);
                    typeCounts[suggestion.type]++;
                }
                if (limitedSuggestions.length >= 8) break;
            }
            
            setSearchSuggestions(limitedSuggestions);
            setShowSuggestions(limitedSuggestions.length > 0);
        };
        
        loadAndGenerateSuggestions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, ownerIndexCache]);

    // Click outside to close suggestions
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchWrapperRef.current && !searchWrapperRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Debounce search query - removed auto-trigger, now only on Enter
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Only clear match counts when search is cleared
        if (!searchQuery.trim()) {
            setDebouncedSearchQuery('');
            setSearchMatchCounts({});
        }
    }, [searchQuery]);

    // Handle search trigger (Enter key or button)
    const triggerSearch = async () => {
        const query = searchQuery.toLowerCase().trim();
        
        // Check if this might be an owner search by looking at the first character
        const firstChar = query[0] && query[0].match(/[a-z0-9]/) ? query[0] : 'other';
        let isOwnerSearch = false;
        
        // Quick check if owner exists in cache
        if (ownerIndexCache[firstChar]) {
            const ownerKey = Object.keys(ownerIndexCache[firstChar]).find(key => key.toLowerCase() === query);
            isOwnerSearch = !!ownerKey;
        }
        
        // If it's an owner search, skip scan animation (use row animation only)
        if (isOwnerSearch) {
            skipScanAnimationRef.current = true;
            setTableFlash(false);
        }
        
        setDebouncedSearchQuery(searchQuery);
        setCurrentPage(1);
        setShowSuggestions(false);
        // Update URL with search
        const newParams = new URLSearchParams(searchParams);
        if (searchQuery.trim()) {
            newParams.set('search', searchQuery);
        } else {
            newParams.delete('search');
        }
        newParams.set('page', '1');
        setSearchParams(newParams);
    };

    // Helper function to update page in URL
    const updatePage = (newPage) => {
        setCurrentPage(newPage);
        const newParams = new URLSearchParams(searchParams);
        newParams.set('page', newPage.toString());
        // Preserve search query in URL
        if (debouncedSearchQuery.trim()) {
            newParams.set('search', debouncedSearchQuery);
        }
        setSearchParams(newParams);
    };

    // Handle owner click - search without showing suggestions, click again to clear
    const handleOwnerClick = (ownerName) => {
        setShowSuggestions(false);
        setSearchSuggestions([]);
        
        // ALWAYS skip scan animation for owner clicks - use row animation only
        skipScanAnimationRef.current = true;
        setTableFlash(false);
        
        // If clicking the same owner, clear search and return to previous page
        if (activeOwner === ownerName) {
            setSearchQuery('');
            setDebouncedSearchQuery('');
            setActiveOwner(null);
            setSearchMatchCounts({});
            setAllSearchResults([]);
            // Return to the page we were on before the search
            const returnPage = pageBeforeSearchRef.current;
            setCurrentPage(returnPage);
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('search');
            newParams.set('page', returnPage.toString());
            setSearchParams(newParams);
        } else {
            // Remember current page before starting new owner search
            pageBeforeSearchRef.current = currentPage;
            // New owner search - reset to page 1
            setSearchQuery(ownerName);
            setDebouncedSearchQuery(ownerName);
            setActiveOwner(ownerName);
            setCurrentPage(1);
            // Update URL with search parameter
            const newParams = new URLSearchParams(searchParams);
            newParams.set('search', ownerName);
            newParams.set('page', '1');
            setSearchParams(newParams);
        }
        
        // Scroll to position between header and search bar after data loads
        setTimeout(() => {
            const headerElement = document.querySelector('header');
            if (headerElement) {
                const headerBottom = headerElement.getBoundingClientRect().bottom + window.pageYOffset;
                // Use requestAnimationFrame for smoother scroll
                requestAnimationFrame(() => {
                    window.scrollTo({ top: headerBottom - 20, behavior: 'smooth' });
                });
            }
        }, 500);
        
        // Trigger table update animation after a tiny delay to let data prepare
        setTimeout(() => {
            setUpdatingRows(true);
            setTimeout(() => setUpdatingRows(false), 600);
        }, 50);
    };

    // Load page data based on selected languages and search
    useEffect(() => {
        if (!metadata) return;

        // Clear tooltip when changing pages or filters
        setHoveredRepo(null);

        const loadData = async () => {
            // Check if filters actually changed (not just page number)
            const filtersChanged = 
                previousFiltersRef.current.search !== debouncedSearchQuery ||
                previousFiltersRef.current.showAll !== showAll ||
                JSON.stringify(previousFiltersRef.current.languages) !== JSON.stringify(selectedLanguages);
            
            // For search results, use client-side pagination (don't reload on page change)
            // But only if we already have the search results loaded
            if (debouncedSearchQuery.trim() && !filtersChanged && allSearchResults.length > 0) {
                // Just update the displayed page from allSearchResults
                const pageSize = 50;
                const startIndex = (currentPage - 1) * pageSize;
                const pageRepos = allSearchResults.slice(startIndex, startIndex + pageSize);
                setRepos(pageRepos);
                if (pageRepos.length > 0) {
                    const pageMax = Math.max(...pageRepos.map(r => r.score));
                    if (pageMax > maxScore) setMaxScore(pageMax);
                }
                return;
            }
            
            // Only show loading for search queries (not for language filters or pagination)
            const isSearchQuery = debouncedSearchQuery.trim() !== '';
            const shouldShowLoading = isSearchQuery && filtersChanged && !skipScanAnimationRef.current;
            
            if (shouldShowLoading) {
                setIsLoading(true);
            }
            
            // Load data FIRST
            if (showAll && !debouncedSearchQuery.trim()) {
                await loadMixedPage();
            } else if (debouncedSearchQuery.trim()) {
                await loadSearchResults();
            } else {
                await loadSelectedLanguages();
            }
            
            // Close loading state after data is ready
            // Use setTimeout to ensure all state updates (including searchMatchCounts) complete first
            if (shouldShowLoading) {
                setTimeout(() => setIsLoading(false), 0);
            }
            
            // THEN trigger animation after data is ready
            // But skip if we're returning from owner click (use row animation instead)
            if (filtersChanged && !skipScanAnimationRef.current) {
                // Clear any existing timeout to ensure immediate restart
                if (tableFlashTimeoutRef.current) {
                    clearTimeout(tableFlashTimeoutRef.current);
                }
                
                // Force restart animation by briefly setting to false then true
                setTableFlash(false);
                requestAnimationFrame(() => {
                    setTableFlash(true);
                    tableFlashTimeoutRef.current = setTimeout(() => {
                        setTableFlash(false);
                        tableFlashTimeoutRef.current = null;
                    }, 2000);
                });
            } else if (filtersChanged && skipScanAnimationRef.current) {
                // Use row animation for owner searches
                setUpdatingRows(true);
                setTimeout(() => setUpdatingRows(false), 600);
            }
            
            // Update previous filters AFTER animation trigger to ensure next comparison is correct
            previousFiltersRef.current = { 
                languages: [...selectedLanguages], 
                showAll, 
                search: debouncedSearchQuery 
            };
            
            // Reset the flag after checking
            skipScanAnimationRef.current = false;
        };

        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [metadata, selectedLanguages, showAll, debouncedSearchQuery, currentPage]);

    // Scroll to position between header and search bar when page changes
    useEffect(() => {
        // Skip scroll on initial page load
        if (isInitialLoadRef.current) return;
        
        // Use setTimeout to ensure DOM is ready
        const timer = setTimeout(() => {
            const headerElement = document.querySelector('header');
            if (headerElement) {
                const headerBottom = headerElement.getBoundingClientRect().bottom + window.pageYOffset;
                window.scrollTo({ top: headerBottom - 20, behavior: 'smooth' });
            }
        }, 100);
        return () => clearTimeout(timer);
    }, [currentPage]);

    // Load mixed page (all languages, directly from pre-generated all/ pages)
    const loadMixedPage = async () => {
        const cacheKey = `mixed_${currentPage}`;
        if (pageCache[cacheKey]) {
            setRepos(pageCache[cacheKey]);
            return;
        }

        // Directly load from pages/all/page_X.json
        const pageUrl = `${process.env.PUBLIC_URL}/pages/all/page_${currentPage}.json`;
        
        try {
            const response = await fetch(pageUrl);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            
            const pageData = await response.json();
            setRepos(pageData);
            setPageCache((prev) => ({ ...prev, [cacheKey]: pageData }));
            
            if (pageData.length > 0) {
                const pageMax = Math.max(...pageData.map((r) => r.score));
                if (pageMax > maxScore) setMaxScore(pageMax);
            }
        } catch (err) {
            console.error(`Failed to load page ${currentPage}:`, err);
            setRepos([]);
        }
    };

    // Load selected languages (need to load and mix multiple languages)
    const loadSelectedLanguages = async () => {
        const cacheKey = `langs_${selectedLanguages.join(",")}_${currentPage}`;
        if (pageCache[cacheKey]) {
            setRepos(pageCache[cacheKey]);
            return;
        }
        const pageSize = 50;
        
        // If only one language selected, directly load from that language's pages (fast path)
        if (selectedLanguages.length === 1) {
            const lang = selectedLanguages[0];
            const langPath = lang.toLowerCase().replace(/\+/g, "plus").replace(/c#/g, "csharp").replace(/c\+\+/g, "cpp");
            const pageUrl = `${process.env.PUBLIC_URL}/pages/${langPath}/page_${currentPage}.json`;
            
            try {
                const response = await fetch(pageUrl);
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                
                const pageData = await response.json();
                // Add category for display
                const reposWithCategory = pageData.map((repo) => ({
                    ...repo,
                    category: lang
                }));
                
                setRepos(reposWithCategory);
                setPageCache((prev) => ({ ...prev, [cacheKey]: reposWithCategory }));
                
                if (reposWithCategory.length > 0) {
                    const pageMax = Math.max(...reposWithCategory.map((r) => r.score));
                    if (pageMax > maxScore) setMaxScore(pageMax);
                }
            } catch (err) {
                console.error(`Failed to load ${lang} page ${currentPage}:`, err);
                setRepos([]);
            }
            
            return;
        }
        
        // For multiple languages, load and mix from their respective pages
        const allReposForLoad = [];

        // Load the current page from each selected language
        for (const lang of selectedLanguages) {
            const langPath = lang.toLowerCase().replace(/\+/g, "plus").replace(/c#/g, "csharp").replace(/c\+\+/g, "cpp");
            const pageUrl = `${process.env.PUBLIC_URL}/pages/${langPath}/page_${currentPage}.json`;

            try {
                const response = await fetch(pageUrl);
                if (!response.ok) continue;
                const pageData = await response.json();
                const reposWithCategory = pageData.map((repo) => ({
                    ...repo,
                    category: lang
                }));
                allReposForLoad.push(...reposWithCategory);
            } catch (err) {
                console.error(`Failed to load ${lang} page ${currentPage}:`, err);
            }
        }

        // Sort by score (descending) for mixed language view
        allReposForLoad.sort((a, b) => b.score - a.score);
        
        // Take top 50 for this page
        const pageRepos = allReposForLoad.slice(0, pageSize);

        setRepos(pageRepos);
        setPageCache((prev) => ({ ...prev, [cacheKey]: pageRepos }));

        if (pageRepos.length > 0) {
            const pageMax = Math.max(...pageRepos.map((r) => r.score));
            if (pageMax > maxScore) setMaxScore(pageMax);
        }
    };

    // Load search results (use owner index for fast exact owner searches)
    const loadSearchResults = async () => {
        const query = debouncedSearchQuery.toLowerCase().trim();
        
        // Try to load owner index for exact match
        const firstChar = query[0] && query[0].match(/[a-z0-9]/) ? query[0] : 'other';
        
        // Load owner index if not cached
        let indexData = ownerIndexCache[firstChar];
        if (!indexData) {
            try {
                console.log(`📥 Loading owner index: ${firstChar}.json`);
                const response = await fetch(`${process.env.PUBLIC_URL}/owner_index/${firstChar}.json`);
                if (response.ok) {
                    indexData = await response.json();
                    setOwnerIndexCache(prev => ({ ...prev, [firstChar]: indexData }));
                    console.log(`✅ Loaded owner index for '${firstChar}'`, Object.keys(indexData).length, 'owners');
                } else {
                    console.log(`❌ Failed to load owner index for '${firstChar}': ${response.status}`);
                }
            } catch (err) {
                console.log(`❌ Error loading owner index for '${firstChar}':`, err);
            }
        }
        
        // Check if this is an exact owner search (case-insensitive)
        let ownerKey = null;
        if (indexData) {
            // Find the actual owner key (case-insensitive match)
            ownerKey = Object.keys(indexData).find(key => key.toLowerCase() === query);
        }
        
        if (ownerKey && indexData[ownerKey]) {
            console.log(`🚀 Using owner index for '${ownerKey}'`);
            let ownerProjects = indexData[ownerKey];
            console.log(`📊 Total projects from index: ${ownerProjects.length}`);
            const matchCounts = {};
            
            // Count by language (include ALL languages including Other)
            const allLangs = [...languages, 'Other'];
            allLangs.forEach(lang => {
                matchCounts[lang] = ownerProjects.filter(p => p.language === lang).length;
            });
            
            console.log(`📈 Match counts:`, matchCounts);
            console.log(`🎯 showAll: ${showAll}, selectedLanguages:`, selectedLanguages);
            
            // Filter by selected languages if not showing all
            if (!showAll && selectedLanguages.length > 0) {
                ownerProjects = ownerProjects.filter(p => selectedLanguages.includes(p.language));
                console.log(`🔍 After language filter: ${ownerProjects.length} projects`);
            }
            
            setSearchMatchCounts(matchCounts);
            
            // Sort by score and store ALL results for client-side pagination
            const allMatchingRepos = [...ownerProjects];
            allMatchingRepos.sort((a, b) => b.score - a.score);
            
            // Store all results
            setAllSearchResults(allMatchingRepos);
            
            // Display first page
            const pageSize = 50;
            const pageRepos = allMatchingRepos.slice(0, pageSize);
            setRepos(pageRepos);
            
            if (pageRepos.length > 0) {
                const pageMax = Math.max(...pageRepos.map(r => r.score));
                if (pageMax > maxScore) setMaxScore(pageMax);
            }
            
            return;
        }
        
        // Fall back to traditional search (for partial matches)
        // But only if query is at least 2 characters (avoid too broad searches)
        // AND only if this is NOT an exact owner click (activeOwner === null means manual search)
        if (activeOwner) {
            // If activeOwner is set but we didn't find in index, no results
            console.log(`⚠️ Owner '${query}' not found in index`);
            setRepos([]);
            setAllSearchResults([]);
            setSearchMatchCounts({});
            return;
        }
        
        if (query.length < 2) {
            console.log(`⏭️ Query too short, skipping search: '${query}'`);
            return;
        }
        
        console.log(`🔍 Searching pages for '${query}'`);
        
        // Check if this is an exact language search (language:XXX)
        const languageMatch = query.match(/^language:(.+)$/i);
        const exactLanguage = languageMatch ? languageMatch[1].trim() : null;
        
        const allMatchingRepos = [];
        const matchCounts = {};

        languages.forEach(lang => {
            matchCounts[lang] = 0;
        });

        const langsToSearch = (showAll || selectedLanguages.length === 0) ? [...languages, 'Other'] : selectedLanguages;
        
        // Load in batches for better performance, but always load enough to get accurate count
        const batchSize = 10; // Load 10 pages at a time per language
        const maxBatches = 10; // Max 100 pages total

        for (let currentBatch = 0; currentBatch < maxBatches; currentBatch++) {
            const batchPromises = langsToSearch.map(async (lang) => {
                const startPage = currentBatch * batchSize + 1;
                const endPage = Math.min(
                    (currentBatch + 1) * batchSize,
                    metadata.languages[lang].pages
                );
                
                if (startPage > metadata.languages[lang].pages) {
                    return { lang, matches: [] };
                }

                const langMatches = [];

                for (let page = startPage; page <= endPage; page++) {
                    try {
                        const langPath = lang.toLowerCase().replace(/\+/g, "plus").replace(/c#/g, "csharp").replace(/c\+\+/g, "cpp");
                        const pageUrl = `${process.env.PUBLIC_URL}/pages/${langPath}/page_${page}.json`;
                        const response = await fetch(pageUrl);
                        const pageData = await response.json();

                        // Apply search filter with relevance scoring
                        const filtered = pageData.filter(repo => {
                            // If exact language search, only match by language field
                            if (exactLanguage) {
                                return repo.language && repo.language.toLowerCase() === exactLanguage.toLowerCase();
                            }
                            
                            // Otherwise, normal fuzzy search
                            const searchFields = [
                                repo.name.toLowerCase(),
                                repo.owner.toLowerCase(),
                                (repo.description || '').toLowerCase(),
                                ...(repo.topics || []).map(t => t.toLowerCase())
                            ];
                            return searchFields.some(field => field.includes(query));
                        }).map(repo => {
                            // Calculate relevance score
                            let relevance = 0;
                            const nameLower = repo.name.toLowerCase();
                            const ownerLower = repo.owner.toLowerCase();
                            const descLower = (repo.description || '').toLowerCase();
                            const topics = (repo.topics || []).map(t => t.toLowerCase());
                            
                            // Exact match in name (highest priority)
                            if (nameLower === query) relevance += 1000;
                            else if (nameLower.includes(query)) relevance += 500;
                            
                            // Exact match in owner
                            if (ownerLower === query) relevance += 800;
                            else if (ownerLower.includes(query)) relevance += 400;
                            
                            // Match in topics
                            if (topics.some(t => t === query)) relevance += 300;
                            else if (topics.some(t => t.includes(query))) relevance += 150;
                            
                            // Match in description (lowest priority)
                            if (descLower.includes(query)) relevance += 50;
                            
                            return { ...repo, category: lang, relevance };
                        });

                        langMatches.push(...filtered);
                    } catch (err) {
                        console.error(`Failed to load ${lang} page ${page}:`, err);
                    }
                }

                return { lang, matches: langMatches };
            });

            // Wait for current batch to complete
            const batchResults = await Promise.all(batchPromises);
            
            // Add results and update counts
            batchResults.forEach(({ lang, matches }) => {
                allMatchingRepos.push(...matches);
                matchCounts[lang] = (matchCounts[lang] || 0) + matches.length;
            });
        }

        // Update match counts for display
        setSearchMatchCounts(matchCounts);

        // Sort by global rank (preserve original ranking)
        allMatchingRepos.sort((a, b) => {
            return (a.global_rank || 999999) - (b.global_rank || 999999);
        });
        
        // Store all results for client-side pagination
        setAllSearchResults(allMatchingRepos);
        
        // Display first page
        const pageSize = 50;
        const pageRepos = allMatchingRepos.slice(0, pageSize);
        setRepos(pageRepos);

        if (pageRepos.length > 0) {
            const pageMax = Math.max(...pageRepos.map((r) => r.score));
            if (pageMax > maxScore) setMaxScore(pageMax);
        }
    };

    // Handle language checkbox toggle
    const handleLanguageToggle = (lang) => {
        // Clear language-specific search when switching languages
        if (searchQuery.match(/^language:.+$/i)) {
            setSearchQuery('');
            setDebouncedSearchQuery('');
            setSearchMatchCounts({});
            setAllSearchResults([]);
        }
        
        // Calculate new state first
        const isCurrentlySelected = selectedLanguages.includes(lang);
        let newLanguages;
        let newShowAll;
        
        if (isCurrentlySelected) {
            // Removing language
            newLanguages = selectedLanguages.filter((l) => l !== lang);
            newShowAll = newLanguages.length === 0;
        } else {
            // Adding language
            newLanguages = [...selectedLanguages, lang];
            newShowAll = false;
        }
        
        // Update all states together
        setSelectedLanguages(newLanguages);
        setShowAll(newShowAll);
        setCurrentPage(1);
        
        // Update URL
        const newParams = new URLSearchParams(searchParams);
        if (newLanguages.length > 0) {
            newParams.set('langs', newLanguages.join(','));
        } else {
            newParams.delete('langs');
        }
        newParams.set('page', '1');
        setSearchParams(newParams);
        
        // Scroll to table position (same as page navigation)
        setTimeout(() => {
            const headerElement = document.querySelector('header');
            if (headerElement) {
                const headerBottom = headerElement.getBoundingClientRect().bottom + window.pageYOffset;
                window.scrollTo({ top: headerBottom - 20, behavior: 'smooth' });
            }
        }, 100);
    };

    // Handle "All" checkbox toggle
    const handleAllToggle = () => {
        if (!showAll) {
            setShowAll(true);
            setSelectedLanguages([]);
            setCurrentPage(1);
            
            // Clear search if it's a language-specific search
            if (searchQuery.match(/^language:.+$/i)) {
                setSearchQuery('');
                setDebouncedSearchQuery('');
                setSearchMatchCounts({});
            }
            
            // Update URL - remove langs param
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('langs');
            newParams.set('page', '1');
            setSearchParams(newParams);
            
            // Scroll to table position (same as page navigation)
            setTimeout(() => {
                const headerElement = document.querySelector('header');
                if (headerElement) {
                    const headerBottom = headerElement.getBoundingClientRect().bottom + window.pageYOffset;
                    window.scrollTo({ top: headerBottom - 20, behavior: 'smooth' });
                }
            }, 100);
        }
    };

    // Calculate total pages
    const getTotalPages = () => {
        const MAX_PROJECTS = 10000;
        const MAX_PAGES = Math.ceil(MAX_PROJECTS / 50); // 200 pages
        
        if (debouncedSearchQuery.trim()) {
            // Search mode: use allSearchResults length for accurate count
            if (allSearchResults.length > 0) {
                return Math.ceil(allSearchResults.length / 50);
            }
            // If still loading, use searchMatchCounts as fallback
            const totalMatches = Object.values(searchMatchCounts).reduce((sum, count) => sum + count, 0);
            return totalMatches > 0 ? Math.ceil(totalMatches / 50) : 1;
        } else if (selectedLanguages.length > 0) {
            // Language filter mode: calculate based on filtered languages
            // Each language shows up to 10000 projects, but total might be less
            const totalProjectsInFilteredLanguages = selectedLanguages.reduce((sum, lang) => {
                return sum + (metadata?.languages?.[lang]?.total || 0);
            }, 0);
            
            // Cap at 10000 projects max
            const actualProjects = Math.min(totalProjectsInFilteredLanguages, MAX_PROJECTS);
            return Math.ceil(actualProjects / 50);
        } else {
            // All projects mode: maximum 200 pages (10000 projects)
            return MAX_PAGES;
        }
    };

    const totalPages = getTotalPages();

    // Fetch repository details when mouse hovers
    const fetchRepoDetails = async (repo) => {
        if (repoDetails[repo.name]) {
            return; // Already fetched
        }

        // Use the data we already have from the JSON (including topics if available)
        setRepoDetails((prev) => ({
            ...prev,
            [repo.name]: {
                description: repo.description || "No description available",
                language: repo.language || "Unknown",
                topics: repo.topics || []
            }
        }));
    };

    // Get repository description
    const getRepoDescription = (repo) => {
        const details = repoDetails[repo.name];
        const watchers = repo.watchers || 0;
        
        if (!details) {
            return `⭐ ${repo.stars.toLocaleString()} stars | 👁️ ${watchers.toLocaleString()} watchers | 🔀 ${repo.forks.toLocaleString()} forks | 🐛 ${repo.issues.toLocaleString()} issues`;
        }

        const hasTechStack = details.topics && details.topics.length > 0;
        return (
            <div>
                <div style={{ marginBottom: "8px" }}>
                    <strong>Language:</strong> {details.language}
                </div>
                {hasTechStack && (
                    <div style={{ marginBottom: "8px" }}>
                        <strong>Tech Stack:</strong>{" "}
                        {details.topics.slice(0, 5).join(", ")}
                    </div>
                )}
                <div style={{ marginBottom: "8px" }}>
                    <strong>Description:</strong> {details.description}
                </div>
                <div>
                    ⭐ {repo.stars.toLocaleString()} stars | 👁️{" "}
                    {watchers.toLocaleString()} watchers | 🔀{" "}
                    {repo.forks.toLocaleString()} forks | 🐛{" "}
                    {repo.issues.toLocaleString()} issues
                </div>
            </div>
        );
    };

    const [lastClickedRepo, setLastClickedRepo] = useState(null);

    function handleBarClick(repo) {
        const isMobile = window.innerWidth <= 768;

        if (!isMobile) {
            window.open(repo.html_url, "_blank");
            return;
        }

        if (lastClickedRepo !== repo.name) {
            setLastClickedRepo(repo.name);
            setHoveredRepo(repo.name);
            return;
        }

        window.open(repo.html_url, "_blank");
    }

    return (
        <div className="container">
            <Link 
                to={{
                    pathname: "/",
                    search: searchParams.toString()
                }}
                className="back-btn"
            >
                ‹ Back
            </Link>

            <header>
                <h1>Seattle Source Ranker</h1>
                <p className="subtitle">
                    Top Open-Source Projects by Seattle-Area Developers
                </p>
                {metadata && metadata.last_updated && (
                    <p className="last-updated">Last updated: {metadata.last_updated}</p>
                )}
            </header>

            {/* Search Bar */}
            <div className="search-container">
                <div className="search-wrapper" ref={searchWrapperRef}>
                    <input
                        type="text"
                        className="search-input"
                        placeholder="🔍 Search projects..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={() => searchSuggestions.length > 0 && setShowSuggestions(true)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                if (selectedSuggestionIndex >= 0 && searchSuggestions.length > 0) {
                                    // Select suggestion - trigger the same logic as clicking
                                    const suggestion = searchSuggestions[selectedSuggestionIndex];
                                    const selectedText = suggestion.text;
                                    
                                    // If it's a language suggestion, filter by that language
                                    if (suggestion.type === 'language') {
                                        setShowSuggestions(false);
                                        setSelectedSuggestionIndex(-1);
                                        setSearchSuggestions([]);
                                        
                                        // Determine which language category to use
                                        const topLanguages = ['JavaScript', 'Python', 'HTML', 'Java', 'Jupyter Notebook', 
                                                              'TypeScript', 'C#', 'Ruby', 'CSS', 'C++'];
                                        
                                        if (topLanguages.includes(selectedText)) {
                                            // It's a top 10 language - use the same logic as clicking the filter checkbox
                                            // Clear search input
                                            setSearchQuery('');
                                            setDebouncedSearchQuery('');
                                            
                                            // Toggle the language (same as handleLanguageToggle)
                                            const newLanguages = [selectedText];
                                            setSelectedLanguages(newLanguages);
                                            setShowAll(false);
                                            setCurrentPage(1);
                                            
                                            // Update URL
                                            const newParams = new URLSearchParams(searchParams);
                                            newParams.set('langs', newLanguages.join(','));
                                            newParams.set('page', '1');
                                            newParams.delete('search');
                                            setSearchParams(newParams);
                                        } else {
                                            // It's from Other category - select Other and search for exact language
                                            const newLanguages = ['Other'];
                                            setSelectedLanguages(newLanguages);
                                            setShowAll(false);
                                            const languageSearch = `language:${selectedText}`;
                                            setSearchQuery(languageSearch);
                                            setDebouncedSearchQuery(languageSearch);
                                            setCurrentPage(1);
                                            
                                            // Update URL
                                            const newParams = new URLSearchParams(searchParams);
                                            newParams.set('langs', newLanguages.join(','));
                                            newParams.set('search', languageSearch);
                                            newParams.set('page', '1');
                                            setSearchParams(newParams);
                                        }
                                    } else {
                                        // For owner/topic suggestions, set active owner and skip scan animation
                                        if (suggestion.type === 'owner') {
                                            // Skip scan animation for owner search (use row animation only)
                                            skipScanAnimationRef.current = true;
                                            setTableFlash(false);
                                            setActiveOwner(selectedText);
                                        }
                                        
                                        setSearchQuery(selectedText);
                                        setDebouncedSearchQuery(selectedText);
                                        setShowSuggestions(false);
                                        setSelectedSuggestionIndex(-1);
                                        setCurrentPage(1);
                                    }
                                } else {
                                    // Trigger search with current input (normal fuzzy search)
                                    triggerSearch();
                                }
                                return;
                            }
                            
                            if (!showSuggestions || searchSuggestions.length === 0) return;
                            
                            if (e.key === 'ArrowDown') {
                                e.preventDefault();
                                setSelectedSuggestionIndex(prev => 
                                    prev < searchSuggestions.length - 1 ? prev + 1 : prev
                                );
                            } else if (e.key === 'ArrowUp') {
                                e.preventDefault();
                                setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
                            } else if (e.key === 'Escape') {
                                setShowSuggestions(false);
                                setSelectedSuggestionIndex(-1);
                            }
                        }}
                    />
                    {searchQuery && (
                        <button
                            className="clear-search-btn"
                            onClick={() => {
                                // Clear any pending hover timeouts
                                if (timeoutRef.current) {
                                    clearTimeout(timeoutRef.current);
                                }
                                
                                // Skip scan animation when clearing search (just show simple transition)
                                skipScanAnimationRef.current = true;
                                
                                // Clear repos to force reload and avoid showing stale search results
                                setRepos([]);
                                setSearchQuery('');
                                setDebouncedSearchQuery('');
                                setHoveredRepo(null); // Clear any open tooltip
                                // Return to the page we were on before the search
                                const returnPage = pageBeforeSearchRef.current;
                                setCurrentPage(returnPage);
                                setPageCache({}); // Clear cache when clearing search
                                setShowSuggestions(false);
                                setSearchMatchCounts({});
                                setActiveOwner(null);
                                // Reset to "All" languages
                                setShowAll(true);
                                setSelectedLanguages([]);
                                // Clear URL parameters
                                const newParams = new URLSearchParams(searchParams);
                                newParams.delete('search');
                                newParams.set('page', returnPage.toString());
                                setSearchParams(newParams);
                                
                                // Trigger row update animation
                                setTimeout(() => {
                                    setUpdatingRows(true);
                                    setTimeout(() => setUpdatingRows(false), 600);
                                }, 50);
                                
                                // Scroll to position between header and search bar
                                setTimeout(() => {
                                    const headerElement = document.querySelector('header');
                                    if (headerElement) {
                                        const headerBottom = headerElement.getBoundingClientRect().bottom + window.pageYOffset;
                                        requestAnimationFrame(() => {
                                            window.scrollTo({ top: headerBottom - 20, behavior: 'smooth' });
                                        });
                                    }
                                }, 500);
                            }}
                            title="Clear search"
                        >
                            ×
                        </button>
                    )}
                    
                    {/* Search Suggestions Dropdown */}
                    {showSuggestions && searchSuggestions.length > 0 && (
                        <div className="search-suggestions">
                            {searchSuggestions.map((suggestion, index) => (
                                <div
                                    key={`${suggestion.type}-${suggestion.text}`}
                                    className={`suggestion-item ${index === selectedSuggestionIndex ? 'selected' : ''}`}
                                    onMouseDown={(e) => {
                                        e.preventDefault(); // Prevent input blur
                                        const selectedText = suggestion.text;
                                        
                                        // If it's a language suggestion, filter by that language
                                        if (suggestion.type === 'language') {
                                            setShowSuggestions(false);
                                            setSelectedSuggestionIndex(-1);
                                            setSearchSuggestions([]);
                                            
                                            // Determine which language category to use
                                            const topLanguages = ['JavaScript', 'Python', 'HTML', 'Java', 'Jupyter Notebook', 
                                                                  'TypeScript', 'C#', 'Ruby', 'CSS', 'C++'];
                                            
                                            if (topLanguages.includes(selectedText)) {
                                                // It's a top 10 language - use the same logic as clicking the filter checkbox
                                                // Clear search input
                                                setSearchQuery('');
                                                setDebouncedSearchQuery('');
                                                
                                                // Toggle the language (same as handleLanguageToggle)
                                                const newLanguages = [selectedText];
                                                setSelectedLanguages(newLanguages);
                                                setShowAll(false);
                                                setCurrentPage(1);
                                                
                                                // Update URL
                                                const newParams = new URLSearchParams(searchParams);
                                                newParams.set('langs', newLanguages.join(','));
                                                newParams.set('page', '1');
                                                newParams.delete('search');
                                                setSearchParams(newParams);
                                            } else {
                                                // It's from Other category - select Other and search for exact language
                                                const newLanguages = ['Other'];
                                                setSelectedLanguages(newLanguages);
                                                setShowAll(false);
                                                const languageSearch = `language:${selectedText}`;
                                                setSearchQuery(languageSearch);
                                                setDebouncedSearchQuery(languageSearch);
                                                setCurrentPage(1);
                                                
                                                // Update URL
                                                const newParams = new URLSearchParams(searchParams);
                                                newParams.set('langs', newLanguages.join(','));
                                                newParams.set('search', languageSearch);
                                                newParams.set('page', '1');
                                                setSearchParams(newParams);
                                            }
                                        } else {
                                            // For owner/topic suggestions, check if it's an owner and skip scan animation
                                            if (suggestion.type === 'owner') {
                                                // Skip scan animation for owner search (use row animation only)
                                                skipScanAnimationRef.current = true;
                                                setTableFlash(false);
                                                setActiveOwner(selectedText);
                                            }
                                            
                                            setSearchQuery(selectedText);
                                            setDebouncedSearchQuery(selectedText);
                                            setShowSuggestions(false);
                                            setSelectedSuggestionIndex(-1);
                                            setSearchSuggestions([]);
                                            setCurrentPage(1);
                                        }
                                    }}
                                    onMouseEnter={() => setSelectedSuggestionIndex(index)}
                                >
                                    <div className="suggestion-left">
                                        <span className="suggestion-icon">{suggestion.icon}</span>
                                        <span className="suggestion-text">{suggestion.text}</span>
                                    </div>
                                    {suggestion.type === 'owner' && (
                                        <span className="suggestion-badge">User</span>
                                    )}
                                    {suggestion.type === 'language' && (
                                        <span className="suggestion-badge">Language</span>
                                    )}
                                    {suggestion.type === 'topic' && (
                                        <span className="suggestion-badge">Topic</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                {isLoading && (
                    <div className="search-hint loading-indicator">
                        <span className="spinner"></span>
                        <span>Loading...</span>
                    </div>
                )}
            </div>

            {/* Language Filter Toggle Button */}
            <button 
                className="filter-toggle-btn"
                onClick={() => {
                    const newShowFilters = !showFilters;
                    setShowFilters(newShowFilters);
                    
                    // Scroll to filter button position when expanding or collapsing
                    setTimeout(() => {
                        const headerElement = document.querySelector('header');
                        if (headerElement) {
                            const headerBottom = headerElement.getBoundingClientRect().bottom + window.pageYOffset;
                            window.scrollTo({ top: headerBottom - 20, behavior: 'smooth' });
                        }
                    }, 100);
                }}
            >
                <span>{showFilters ? '▼' : '▶'}</span> Filter by Language
                {!showAll && selectedLanguages.length > 0 && !searchQuery.match(/^language:.+$/i) && (
                    <span className="active-filter-badge">{selectedLanguages.length}</span>
                )}
            </button>

            {/* Language Filter Checkboxes */}
            {showFilters && (
                <div className="language-filters">
                    <div className="checkbox-grid">
                        {/* All option */}
                        <label className="checkbox-label checkbox-label-all">
                            <input
                                type="checkbox"
                                checked={showAll}
                                onChange={handleAllToggle}
                            />
                            <span className="checkbox-text">
              <strong>All</strong>
                                {metadata && (
                                    <span className="lang-count">
                                        {debouncedSearchQuery.trim() && Object.keys(searchMatchCounts).length > 0 ? (
                                            `(${Object.values(searchMatchCounts).reduce((sum, count) => sum + count, 0).toLocaleString()} matches)`
                                        ) : (
                                            `(${Object.keys(metadata.languages).filter(l => l !== 'Python_PyPI' && l !== 'All').reduce((sum, lang) => sum + (metadata.languages[lang]?.total || 0), 0).toLocaleString()})`
                                        )}
                </span>
                                )}
            </span>
                        </label>

                        {/* Individual languages */}
                        {languages.map((lang) => (
                            <label key={lang} className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={selectedLanguages.includes(lang)}
                                    onChange={() => handleLanguageToggle(lang)}
                                />
                                <span className="checkbox-text">
                {lang}
                                    {metadata && metadata.languages[lang] && (
                                        <span className="lang-count">
                                            {debouncedSearchQuery.trim() ? (
                                                // During search, show match count if available, otherwise show original count
                                                searchMatchCounts[lang] !== undefined ?
                                                    `(${searchMatchCounts[lang].toLocaleString()} ${searchMatchCounts[lang] === 1 ? 'match' : 'matches'})` :
                                                    `(${metadata.languages[lang].total.toLocaleString()})`
                                            ) : (
                                                `(${metadata.languages[lang].total.toLocaleString()})`
                                            )}
                  </span>
                                    )}
              </span>
                            </label>
                        ))}
                    </div>
                </div>
            )}

            {/* Ranking Table */}
            <div className={`ranking-table ${tableFlash ? 'table-flash' : ''}`}>
                <table>
                    <thead>
                    <tr>
                        <th className="rank-col">#</th>
                        <th className="owner-col">Owner</th>
                        <th className="chart-col">Project Name</th>
                        <th className="score-col">Score</th>
                    </tr>
                    </thead>
                    <tbody>
                    {repos.map((repo, index) => {
                        // Use global rank if available, otherwise calculate from page
                        const displayRank = repo.global_rank || ((currentPage - 1) * 50 + index + 1);
                        // Use min-max normalization if we have topScore and minScore
                        let barWidth;
                        if (topScore !== null && minScore !== null && topScore !== minScore) {
                            barWidth = ((repo.score - minScore) / (topScore - minScore)) * 100;
                        } else {
                            // Fallback to linear scale if scores not loaded yet
                            barWidth = (repo.score / maxScore) * 100;
                        }
                        // Display score directly (backend now returns 0-1000000 range)
                        const displayScore = repo.score.toLocaleString();

                        // Extract project name (remove owner/ prefix)
                        const projectName = repo.name.includes("/")
                            ? repo.name.split("/")[1]
                            : repo.name;

                        return (
                            <tr 
                                key={`${repo.name}-${displayRank}`}
                                className={updatingRows ? 'row-updating' : ''}
                                style={updatingRows ? { animationDelay: `${index * 0.03}s` } : {}}
                            >
                                <td className="rank-col">#{displayRank}</td>
                                <td className="owner-col">
                                    <span
                                        className={`owner-link ${activeOwner === repo.owner ? 'owner-active' : ''}`}
                                        onClick={() => handleOwnerClick(repo.owner)}
                                        title={activeOwner === repo.owner ? `Click to clear search` : `Search for ${repo.owner}`}
                                    >
                                        {repo.owner}
                                    </span>
                                </td>
                                <td className="chart-col">
                                    <div className="bar-container">
                                        <div 
                                            className="bar-wrapper-column"
                                            // onClick={() => window.open(repo.html_url, '_blank')}
                                            onClick={() => handleBarClick(repo)}
                                            style={{ cursor: 'pointer' }}
                                            onMouseEnter={(e) => {
                                                // Clear any pending close operations
                                                if (timeoutRef.current) {
                                                    clearTimeout(timeoutRef.current);
                                                }
                                                
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                const tooltipHeight = 200;
                                                const viewportHeight = window.innerHeight;
                                                const spaceBelow = viewportHeight - rect.bottom;
                                                const showAbove = spaceBelow < tooltipHeight + 20;
                                                
                                                setTooltipPosition({
                                                    [repo.name]: showAbove
                                                });
                                                setHoveredRepo(repo.name);
                                                fetchRepoDetails(repo);
                                            }}
                                            onMouseLeave={() => {
                                                timeoutRef.current = setTimeout(() => {
                                                    setHoveredRepo(null);
                                                }, 150);
                                            }}
                                        >
                                            <div
                                                className="bar"
                                                style={{ width: `${barWidth}%` }}
                                            >
                                            </div>
                                            <span className="project-name-below">
                                                {projectName}
                                            </span>
                                        </div>
                                        {hoveredRepo === repo.name && (
                                            <div
                                                className={`tooltip ${tooltipPosition[repo.name] ? 'tooltip-above' : ''}`}
                                                onMouseEnter={() => {
                                                    // Clear close operation
                                                    if (timeoutRef.current) {
                                                        clearTimeout(timeoutRef.current);
                                                    }
                                                    setHoveredRepo(repo.name);
                                                }}
                                                onMouseLeave={() => {
                                                    setHoveredRepo(null);
                                                }}
                                            >
                                                <div className="tooltip-title">
                                                    {projectName}
                                                </div>
                                                <div className="tooltip-desc">
                                                    {getRepoDescription(repo)}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </td>
                                <td className="score-col">{displayScore}</td>
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </div>

            {/* Page Info */}
            <div
                style={{
                    textAlign: "center",
                    margin: "20px 0 15px",
                    color: "#999",
                    fontSize: "0.95em"
                }}
            >
                {showAll && !debouncedSearchQuery.trim() ? (
                    <>
                        Showing {(currentPage - 1) * 50 + 1}-
                        {Math.min(
                            currentPage * 50,
                            10000 // Max displayable projects
                        )}{" "}
                        of{" "}
                        {Math.min(
                            metadata
                                ? Object.keys(metadata.languages).filter(l => l !== 'Python_PyPI' && l !== 'All').reduce(
                                    (sum, lang) => sum + metadata.languages[lang].total,
                                    0
                                )
                                : 0,
                            10000
                        ).toLocaleString()}{" "}
                        projects
                    </>
                ) : !debouncedSearchQuery.trim() &&
                selectedLanguages.length > 0 ? (
                    <>
                        Showing {(currentPage - 1) * 50 + 1}-
                        {Math.min(
                            currentPage * 50,
                            Math.min(
                                selectedLanguages.reduce(
                                    (sum, lang) =>
                                        sum + (metadata.languages[lang]?.total || 0),
                                    0
                                ),
                                10000
                            )
                        )}{" "}
                        of{" "}
                        {Math.min(
                            selectedLanguages.reduce(
                                (sum, lang) => sum + (metadata.languages[lang]?.total || 0),
                                0
                            ),
                            10000
                        ).toLocaleString()}{" "}
                        projects
                    </>
                ) : debouncedSearchQuery.trim() && Object.keys(searchMatchCounts).length > 0 ? (
                    <>
                        Showing {repos.length > 0 ? (currentPage - 1) * 50 + 1 : 0}-
                        {repos.length > 0
                            ? (currentPage - 1) * 50 + repos.length
                            : 0}{" "}
                        of {Object.values(searchMatchCounts).reduce((sum, count) => sum + count, 0).toLocaleString()} matches
                    </>
                ) : (
                    <>
                        Showing {repos.length > 0 ? (currentPage - 1) * 50 + 1 : 0}-
                        {repos.length > 0
                            ? (currentPage - 1) * 50 + repos.length
                            : 0}{" "}
                        {isLoading && <span className="spinner" style={{display: 'inline-block', marginLeft: '8px'}}></span>}
                    </>
                )}
            </div>

            {/* Pagination - hide during search loading until results are ready */}
            {totalPages > 1 && !(debouncedSearchQuery.trim() && isLoading) && (
                <div className="pagination-container">
                    <button
                        className="pagination-btn pagination-edge"
                        onClick={() => {
                            updatePage(1);
                            setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                        }}
                        disabled={currentPage === 1 || isLoading}
                        title="First page"
                    >
                        «
                    </button>
                    
                    <button
                        className="pagination-btn"
                        onClick={() => updatePage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1 || isLoading}
                        title="Previous page"
                    >
                        ‹
                    </button>
                    
                    <div className="page-input-wrapper">
                        <input
                            type="number"
                            className="page-input"
                            value={pageInput !== null ? pageInput : currentPage}
                            onChange={(e) => setPageInput(e.target.value)}
                            onFocus={(e) => {
                                if (pageInput === null) {
                                    setPageInput(currentPage.toString());
                                    setTimeout(() => e.target.select(), 0);
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    const pageNum = parseInt(pageInput);
                                    if (pageNum >= 1 && pageNum <= totalPages) {
                                        updatePage(pageNum);
                                        setPageInput(null);
                                    }
                                    e.target.blur();
                                } else if (e.key === 'Escape') {
                                    setPageInput(null);
                                    e.target.blur();
                                } else if (e.key === 'ArrowUp') {
                                    e.preventDefault();
                                    const current = parseInt(pageInput) || currentPage;
                                    const newPage = Math.min(totalPages, current + 1);
                                    setPageInput(newPage.toString());
                                } else if (e.key === 'ArrowDown') {
                                    e.preventDefault();
                                    const current = parseInt(pageInput) || currentPage;
                                    const newPage = Math.max(1, current - 1);
                                    setPageInput(newPage.toString());
                                }
                            }}
                            onBlur={() => {
                                setPageInput(null);
                            }}
                            min="1"
                            max={totalPages}
                            disabled={isLoading}
                        />
                        <span className="page-total">/ {totalPages.toLocaleString()}</span>
                    </div>
                    
                    <button
                        className="pagination-btn"
                        onClick={() => updatePage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages || isLoading}
                        title="Next page"
                    >
                        ›
                    </button>
                    
                    <button
                        className="pagination-btn pagination-edge"
                        onClick={() => updatePage(totalPages)}
                        disabled={currentPage === totalPages || isLoading}
                        title="Last page"
                    >
                        »
                    </button>
                </div>
            )}
        </div>
    );
}
